import React, { useState } from 'react';
import { PhoneIcon, MailIcon, CalendarIcon, SendIcon, XIcon, CheckCircle2Icon } from 'lucide-react';
import { Activity, ActivityType, CallDisposition, EmailStatus } from '../data/mockData';
interface CreateActivityFormProps {
  onAddActivity: (activity: Activity) => void;
}
type ActiveFormType = 'call' | 'email' | 'event' | null;
interface CallFormState {
  summary: string;
  disposition: CallDisposition;
  durationMinutes: string;
  durationSeconds: string;
  isMeaningful: boolean;
  details: string;
}
interface EmailFormState {
  summary: string;
  emailStatus: EmailStatus;
  details: string;
}
interface EventFormState {
  summary: string;
  eventType: string;
  details: string;
}
const INITIAL_CALL: CallFormState = {
  summary: '',
  disposition: 'Connected',
  durationMinutes: '',
  durationSeconds: '',
  isMeaningful: false,
  details: ''
};
const INITIAL_EMAIL: EmailFormState = {
  summary: '',
  emailStatus: 'sent',
  details: ''
};
const INITIAL_EVENT: EventFormState = {
  summary: '',
  eventType: '',
  details: ''
};
const ACTION_BUTTONS: {
  type: 'call' | 'email' | 'event';
  label: string;
  icon: React.ReactNode;
  bgActive: string;
  bgInactive: string;
  textActive: string;
  textInactive: string;
  ringColor: string;
}[] = [{
  type: 'call',
  label: 'Log Call',
  icon: <PhoneIcon className="w-4 h-4" data-id="element-49" />,
  bgActive: 'bg-emerald-600',
  bgInactive: 'bg-white',
  textActive: 'text-white',
  textInactive: 'text-emerald-700',
  ringColor: 'focus-visible:ring-emerald-500'
}, {
  type: 'email',
  label: 'Log Email',
  icon: <MailIcon className="w-4 h-4" data-id="element-50" />,
  bgActive: 'bg-blue-600',
  bgInactive: 'bg-white',
  textActive: 'text-white',
  textInactive: 'text-blue-700',
  ringColor: 'focus-visible:ring-blue-500'
}, {
  type: 'event',
  label: 'Log Event',
  icon: <CalendarIcon className="w-4 h-4" data-id="element-51" />,
  bgActive: 'bg-indigo-600',
  bgInactive: 'bg-white',
  textActive: 'text-white',
  textInactive: 'text-indigo-700',
  ringColor: 'focus-visible:ring-indigo-500'
}];
const TYPE_ICON_CONFIG: Record<'call' | 'email' | 'event', {
  icon: React.ReactNode;
  bg: string;
}> = {
  call: {
    icon: <PhoneIcon className="w-5 h-5" data-id="element-52" />,
    bg: 'bg-emerald-100 text-emerald-600 ring-emerald-50'
  },
  email: {
    icon: <MailIcon className="w-5 h-5" data-id="element-53" />,
    bg: 'bg-blue-100 text-blue-600 ring-blue-50'
  },
  event: {
    icon: <CalendarIcon className="w-5 h-5" data-id="element-54" />,
    bg: 'bg-indigo-100 text-indigo-600 ring-indigo-50'
  }
};
const TYPE_BORDER_COLOR: Record<'call' | 'email' | 'event', string> = {
  call: 'border-t-emerald-500',
  email: 'border-t-blue-500',
  event: 'border-t-indigo-500'
};
const TYPE_SUBMIT_COLOR: Record<'call' | 'email' | 'event', string> = {
  call: 'bg-emerald-600 hover:bg-emerald-700 focus-visible:ring-emerald-500',
  email: 'bg-blue-600 hover:bg-blue-700 focus-visible:ring-blue-500',
  event: 'bg-indigo-600 hover:bg-indigo-700 focus-visible:ring-indigo-500'
};
export function CreateActivityForm({
  onAddActivity
}: CreateActivityFormProps) {
  const [activeForm, setActiveForm] = useState<ActiveFormType>(null);
  const [callForm, setCallForm] = useState<CallFormState>(INITIAL_CALL);
  const [emailForm, setEmailForm] = useState<EmailFormState>(INITIAL_EMAIL);
  const [eventForm, setEventForm] = useState<EventFormState>(INITIAL_EVENT);
  const handleToggleForm = (type: 'call' | 'email' | 'event') => {
    if (activeForm === type) {
      setActiveForm(null);
    } else {
      setActiveForm(type);
    }
  };
  const resetForm = (type: 'call' | 'email' | 'event') => {
    switch (type) {
      case 'call':
        setCallForm(INITIAL_CALL);
        break;
      case 'email':
        setEmailForm(INITIAL_EMAIL);
        break;
      case 'event':
        setEventForm(INITIAL_EVENT);
        break;
    }
  };
  const handleCancel = () => {
    if (activeForm) {
      resetForm(activeForm);
    }
    setActiveForm(null);
  };
  const handleSubmitCall = (e: React.FormEvent) => {
    e.preventDefault();
    if (!callForm.summary.trim()) return;
    const mins = parseInt(callForm.durationMinutes) || 0;
    const secs = parseInt(callForm.durationSeconds) || 0;
    const totalSeconds = mins * 60 + secs;
    const activity: Activity = {
      id: `call-${Date.now()}`,
      type: 'call',
      timestamp: new Date().toISOString(),
      summary: callForm.summary.trim(),
      details: callForm.details.trim() || undefined,
      disposition: callForm.disposition,
      duration: totalSeconds > 0 ? totalSeconds : undefined,
      isMeaningful: callForm.isMeaningful
    };
    onAddActivity(activity);
    resetForm('call');
    setActiveForm(null);
  };
  const handleSubmitEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailForm.summary.trim()) return;
    const activity: Activity = {
      id: `email-${Date.now()}`,
      type: 'email',
      timestamp: new Date().toISOString(),
      summary: emailForm.summary.trim(),
      details: emailForm.details.trim() || undefined,
      emailStatus: emailForm.emailStatus
    };
    onAddActivity(activity);
    resetForm('email');
    setActiveForm(null);
  };
  const handleSubmitEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventForm.summary.trim()) return;
    const activity: Activity = {
      id: `event-${Date.now()}`,
      type: 'event',
      timestamp: new Date().toISOString(),
      summary: eventForm.summary.trim(),
      details: eventForm.details.trim() || undefined,
      eventType: eventForm.eventType.trim() || undefined
    };
    onAddActivity(activity);
    resetForm('event');
    setActiveForm(null);
  };
  const currentIconConfig = activeForm ? TYPE_ICON_CONFIG[activeForm] : TYPE_ICON_CONFIG.call;
  const inputClasses = 'w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent placeholder-gray-400 transition-shadow';
  const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5';
  const selectClasses = 'w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent appearance-none cursor-pointer transition-shadow';
  const textareaClasses = 'w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-0 focus:border-transparent placeholder-gray-400 resize-none transition-shadow';
  const getFocusRing = (type: 'call' | 'email' | 'event') => {
    switch (type) {
      case 'call':
        return 'focus:ring-emerald-500';
      case 'email':
        return 'focus:ring-blue-500';
      case 'event':
        return 'focus:ring-indigo-500';
    }
  };
  const renderCallForm = () => <form onSubmit={handleSubmitCall} className="space-y-4" data-id="element-55">
      <div data-id="element-56">
        <label htmlFor="call-summary" className={labelClasses} data-id="element-57">
          Summary <span className="text-red-500" data-id="element-58">*</span>
        </label>
        <input id="call-summary" type="text" value={callForm.summary} onChange={e => setCallForm(p => ({
        ...p,
        summary: e.target.value
      }))} placeholder="e.g. Outbound Call - Follow up on documents" className={`${inputClasses} ${getFocusRing('call')}`} required autoFocus data-id="element-59" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-60">
        <div data-id="element-61">
          <label htmlFor="call-disposition" className={labelClasses} data-id="element-62">
            Disposition <span className="text-red-500" data-id="element-63">*</span>
          </label>
          <select id="call-disposition" value={callForm.disposition} onChange={e => setCallForm(p => ({
          ...p,
          disposition: e.target.value as CallDisposition
        }))} className={`${selectClasses} ${getFocusRing('call')}`} data-id="element-64">
            <option value="Connected" data-id="element-65">Connected</option>
            <option value="Voicemail" data-id="element-66">Voicemail</option>
            <option value="No Answer" data-id="element-67">No Answer</option>
            <option value="Busy" data-id="element-68">Busy</option>
            <option value="Left Message" data-id="element-69">Left Message</option>
          </select>
        </div>

        <div data-id="element-70">
          <label className={labelClasses} data-id="element-71">Duration (optional)</label>
          <div className="flex items-center gap-2" data-id="element-72">
            <div className="relative flex-1" data-id="element-73">
              <input type="number" min="0" max="999" value={callForm.durationMinutes} onChange={e => setCallForm(p => ({
              ...p,
              durationMinutes: e.target.value
            }))} placeholder="0" className={`${inputClasses} ${getFocusRing('call')} pr-10`} aria-label="Duration minutes" data-id="element-74" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none" data-id="element-75">
                min
              </span>
            </div>
            <span className="text-gray-400 font-medium" data-id="element-76">:</span>
            <div className="relative flex-1" data-id="element-77">
              <input type="number" min="0" max="59" value={callForm.durationSeconds} onChange={e => setCallForm(p => ({
              ...p,
              durationSeconds: e.target.value
            }))} placeholder="0" className={`${inputClasses} ${getFocusRing('call')} pr-10`} aria-label="Duration seconds" data-id="element-78" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none" data-id="element-79">
                sec
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3" data-id="element-80">
        <input id="call-meaningful" type="checkbox" checked={callForm.isMeaningful} onChange={e => setCallForm(p => ({
        ...p,
        isMeaningful: e.target.checked
      }))} className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer" data-id="element-81" />
        <label htmlFor="call-meaningful" className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-id="element-82">
          <CheckCircle2Icon className="w-4 h-4 text-emerald-500" data-id="element-83" />
          Meaningful interaction
        </label>
      </div>

      <div data-id="element-84">
        <label htmlFor="call-details" className={labelClasses} data-id="element-85">
          Notes (optional)
        </label>
        <textarea id="call-details" value={callForm.details} onChange={e => setCallForm(p => ({
        ...p,
        details: e.target.value
      }))} placeholder="Add call notes or key takeaways..." className={`${textareaClasses} ${getFocusRing('call')}`} rows={3} data-id="element-86" />
      </div>

      {renderFormActions('call', !callForm.summary.trim())}
    </form>;
  const renderEmailForm = () => <form onSubmit={handleSubmitEmail} className="space-y-4" data-id="element-87">
      <div data-id="element-88">
        <label htmlFor="email-summary" className={labelClasses} data-id="element-89">
          Subject <span className="text-red-500" data-id="element-90">*</span>
        </label>
        <input id="email-summary" type="text" value={emailForm.summary} onChange={e => setEmailForm(p => ({
        ...p,
        summary: e.target.value
      }))} placeholder="e.g. Action Required: Upload Missing Documents" className={`${inputClasses} ${getFocusRing('email')}`} required autoFocus data-id="element-91" />
      </div>

      <div data-id="element-92">
        <label htmlFor="email-status" className={labelClasses} data-id="element-93">
          Status
        </label>
        <select id="email-status" value={emailForm.emailStatus} onChange={e => setEmailForm(p => ({
        ...p,
        emailStatus: e.target.value as EmailStatus
      }))} className={`${selectClasses} ${getFocusRing('email')}`} data-id="element-94">
          <option value="sent" data-id="element-95">Sent</option>
          <option value="opened" data-id="element-96">Opened</option>
        </select>
      </div>

      <div data-id="element-97">
        <label htmlFor="email-details" className={labelClasses} data-id="element-98">
          Body (optional)
        </label>
        <textarea id="email-details" value={emailForm.details} onChange={e => setEmailForm(p => ({
        ...p,
        details: e.target.value
      }))} placeholder="Email body or summary of content..." className={`${textareaClasses} ${getFocusRing('email')}`} rows={4} data-id="element-99" />
      </div>

      {renderFormActions('email', !emailForm.summary.trim())}
    </form>;
  const renderEventForm = () => <form onSubmit={handleSubmitEvent} className="space-y-4" data-id="element-100">
      <div data-id="element-101">
        <label htmlFor="event-summary" className={labelClasses} data-id="element-102">
          Summary <span className="text-red-500" data-id="element-103">*</span>
        </label>
        <input id="event-summary" type="text" value={eventForm.summary} onChange={e => setEventForm(p => ({
        ...p,
        summary: e.target.value
      }))} placeholder="e.g. Appraisal Scheduled" className={`${inputClasses} ${getFocusRing('event')}`} required autoFocus data-id="element-104" />
      </div>

      <div data-id="element-105">
        <label htmlFor="event-type" className={labelClasses} data-id="element-106">
          Event Type (optional)
        </label>
        <input id="event-type" type="text" value={eventForm.eventType} onChange={e => setEventForm(p => ({
        ...p,
        eventType: e.target.value
      }))} placeholder="e.g. Property Appraisal, Closing, Inspection" className={`${inputClasses} ${getFocusRing('event')}`} data-id="element-107" />
      </div>

      <div data-id="element-108">
        <label htmlFor="event-details" className={labelClasses} data-id="element-109">
          Details (optional)
        </label>
        <textarea id="event-details" value={eventForm.details} onChange={e => setEventForm(p => ({
        ...p,
        details: e.target.value
      }))} placeholder="Add event details..." className={`${textareaClasses} ${getFocusRing('event')}`} rows={3} data-id="element-110" />
      </div>

      {renderFormActions('event', !eventForm.summary.trim())}
    </form>;
  const renderFormActions = (type: 'call' | 'email' | 'event', isDisabled: boolean) => <div className="flex justify-end items-center gap-3 pt-2" data-id="element-111">
      <button type="button" onClick={handleCancel} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded-md px-3 py-2" data-id="element-112">
        Cancel
      </button>
      <button type="submit" disabled={isDisabled} className={`inline-flex items-center px-5 py-2.5 text-sm font-medium text-white rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm ${TYPE_SUBMIT_COLOR[type]}`} data-id="element-113">
        <SendIcon className="w-4 h-4 mr-2" data-id="element-114" />
        {type === 'call' ? 'Log Call' : type === 'email' ? 'Log Email' : 'Log Event'}
      </button>
    </div>;
  const renderActiveForm = () => {
    switch (activeForm) {
      case 'call':
        return renderCallForm();
      case 'email':
        return renderEmailForm();
      case 'event':
        return renderEventForm();
      default:
        return null;
    }
  };
  return <div className="relative flex gap-6 mb-8" data-id="element-115">
      {/* Timeline Line Connection */}
      <div className="absolute left-6 top-12 bottom-0 w-px bg-gray-200 -ml-px" data-id="element-116" />

      {/* Timeline Icon */}
      <div className="relative z-10 flex-shrink-0" data-id="element-117">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ring-4 transition-colors duration-200 ${currentIconConfig.bg}`} data-id="element-118">
          {currentIconConfig.icon}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1" data-id="element-119">
        {/* Action Buttons Row */}
        <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-120">
          {ACTION_BUTTONS.map(btn => {
          const isActive = activeForm === btn.type;
          return <button key={btn.type} onClick={() => handleToggleForm(btn.type)} className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${btn.ringColor} ${isActive ? `${btn.bgActive} ${btn.textActive} border-transparent shadow-sm` : `${btn.bgInactive} ${btn.textInactive} border-gray-200 hover:border-gray-300 hover:shadow-sm`}`} aria-pressed={isActive} aria-label={btn.label} data-id="element-121">
                {btn.icon}
                <span className="hidden sm:inline" data-id="element-122">{btn.label}</span>
              </button>;
        })}

          {activeForm && <button onClick={handleCancel} className="ml-auto inline-flex items-center gap-1 px-2 py-2 text-sm text-gray-400 hover:text-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 rounded-md" aria-label="Close form" data-id="element-123">
              <XIcon className="w-4 h-4" data-id="element-124" />
            </button>}
        </div>

        {/* Inline Form */}
        {activeForm && <div className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden border-t-4 ${TYPE_BORDER_COLOR[activeForm]}`} data-id="element-125">
            <div className="p-5" data-id="element-126">{renderActiveForm()}</div>
          </div>}
      </div>
    </div>;
}